import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import { useApi } from "../../utils/api";
import { useDebounce } from "../../utils/hooks";
import AddWorkoutDetails from "../AddWorkoutDetails";
import ReviewClientActivity from "./ReviewClientActivity";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  Box,
  Typography,
  IconButton,
  Grid2,
  Container,
  Menu,
  MenuItem,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";

const theme = createTheme({
  typography: {
    fontFamily: '"Roboto Mono", monospace',
  },
  palette: {
    primary: {
      main: "#ff0000",
    },
  },
});

const WorkoutCalendarView = ({ clientId }) => {
  const [workouts, setWorkouts] = useState([]);
  const [currentDate, setCurrentDate] = useState(moment());
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const {
    getWorkoutsForUser,
    saveWorkoutDetails,
    addWorkoutForUser,
    startConversation,
    getUser,
  } = useApi();
  const auth = getAuth();
  const [client, setClient] = useState(null);

  useEffect(() => {
    if (clientId) {
      debouncedFetchWorkouts(clientId);
      debouncedFetchClient(clientId);
    }
  }, [clientId, currentDate, debouncedFetchWorkouts, debouncedFetchClient]);

  const fetchClient = useCallback(
    async (id) => {
      try {
        const response = await getUser(id);
        setClient(response);
      } catch (error) {
        console.error("Error fetching client:", error);
      }
    },
    [getUser]
  );

  const debouncedFetchClient = useDebounce(fetchClient, 300);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleRepeatLastWeek = async () => {
    try {
      const response = await getWorkoutsForUser(
        clientId,
        moment().subtract(12, "month").unix(),
        moment().add(12, "month").unix()
      );

      if (!response.length) {
        alert("No workouts found to repeat");
        return;
      }

      const formattedWorkouts = response.map((workout) => ({
        ...workout,
        start: moment.unix(workout.date).startOf("day").toDate(),
      }));

      const sortedWorkouts = formattedWorkouts.sort(
        (a, b) => moment(b.start).valueOf() - moment(a.start).valueOf()
      );

      const mostRecentDate = moment(sortedWorkouts[0].start);
      const mostRecentWeekWorkouts = sortedWorkouts.filter((workout) =>
        moment(workout.start).isSame(mostRecentDate, "week")
      );

      const newWorkouts = mostRecentWeekWorkouts.map((workout) => ({
        ...workout,
        date: moment(workout.start).add(7, "days").unix(),
      }));

      await Promise.all(
        newWorkouts.map((workout) => addWorkoutForUser(clientId, workout))
      );

      handleCloseMenu();
      debouncedFetchWorkouts(clientId);
    } catch (error) {
      console.error("Error repeating last week's workouts:", error);
      alert("Failed to repeat last week's workouts. Please try again.");
    }
  };

  const handleCreateConversation = async () => {
    try {
      handleCloseMenu();
      await startConversation({
        name: `Chat with ${client?.name}`,
        participantIds: [clientId],
      });
      alert("Conversation created successfully!");
    } catch (error) {
      console.error("Failed to create conversation:", error);
      alert(`Error creating conversation: ${error.message || "Unknown error"}`);
    }
  };

  const menuItems = (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleCloseMenu}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <MenuItem onClick={handleRepeatLastWeek}>Repeat Last Week</MenuItem>
      <MenuItem onClick={handleCreateConversation}>
        Create Conversation
      </MenuItem>
      <MenuItem
        onClick={() => {
          if (client?.email) {
            sendPasswordResetEmail(auth, client.email);
            handleCloseMenu();
          }
        }}
      >
        Reset Password
      </MenuItem>
    </Menu>
  );

  const fetchWorkouts = useCallback(
    async (id, customDate = null) => {
      try {
        const date = customDate || currentDate;
        const startOfMonth = date.clone().startOf("month");
        const endOfMonth = date.clone().endOf("month");

        const response = await getWorkoutsForUser(
          id,
          startOfMonth.unix(),
          endOfMonth.unix()
        );

        const formattedWorkouts = response.map((workout) => ({
          ...workout,
          date: moment.unix(workout.date),
          user: { name: "Client" },
        }));

        setWorkouts(formattedWorkouts);
      } catch (error) {
        console.error("Error fetching workouts:", error);
      }
    },
    [getWorkoutsForUser, currentDate]
  );

  const debouncedFetchWorkouts = useDebounce(fetchWorkouts, 300);

  const handlePrevMonth = () => {
    const newDate = currentDate.clone().subtract(1, "month");
    setCurrentDate(newDate);
    // Fetch workouts for the new month
    debouncedFetchWorkouts(clientId, newDate);
  };

  const handleNextMonth = () => {
    const newDate = currentDate.clone().add(1, "month");
    setCurrentDate(newDate);
    // Fetch workouts for the new month
    debouncedFetchWorkouts(clientId, newDate);
  };

  const handleWorkoutClick = (workout, date) => {
    setSelectedWorkout(workout || { date });
  };

  const handleSaveWorkout = async (workoutData) => {
    try {
      if (selectedWorkout?.id) {
        await saveWorkoutDetails(selectedWorkout.id, workoutData);
      } else {
        await addWorkoutForUser(clientId, workoutData);
      }
      await debouncedFetchWorkouts(clientId);
      setSelectedWorkout(null);
    } catch (error) {
      console.error("Error saving workout:", error);
    }
  };

  const renderWorkoutContent = (workout) => {
    if (!workout) return null;

    if (workout.id) {
      return (
        <ReviewClientActivity
          reviewingActivity={{ ...workout, user: { name: "Client" } }}
          activityLoading={false}
          hideReviewButton={true}
        />
      );
    } else {
      return (
        <AddWorkoutDetails
          open={true}
          handleClose={() => setSelectedWorkout(null)}
          userId={clientId}
          workout={workout}
          onSave={handleSaveWorkout}
          onWorkoutCopied={() => debouncedFetchWorkouts(clientId)}
          onWorkoutDeleted={() => debouncedFetchWorkouts(clientId)}
          onWorkoutUpdated={() => debouncedFetchWorkouts(clientId)}
          inline={true}
        />
      );
    }
  };

  const renderCalendar = () => {
    const startOfMonth = currentDate.clone().startOf("month");
    const endOfMonth = currentDate.clone().endOf("month");
    const daysInMonth = endOfMonth.date();
    const firstDayOfMonth = startOfMonth.day();

    const weeks = [];
    let currentWeek = [];
    let currentDay = 1;

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      currentWeek.push(
        <Grid2 item xs={12 / 7} key={`empty-${i}`} sx={{ flex: 1 }}>
          <Box
            sx={{
              height: "500px",
              p: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          />
        </Grid2>
      );
    }

    // Add cells for each day of the month
    while (currentDay <= daysInMonth) {
      if (currentWeek.length === 7) {
        weeks.push(
          <Grid2
            container
            spacing={0}
            key={`week-${weeks.length}`}
            justifyContent="space-between"
          >
            {currentWeek}
          </Grid2>
        );
        currentWeek = [];
      }

      const currentDate = moment(startOfMonth).date(currentDay);
      const workout = workouts.find((w) => w.date.isSame(currentDate, "day"));

      currentWeek.push(
        <Grid2
          item
          xs={12 / 7}
          justifyContent="space-between"
          key={`day-${currentDay}`}
          sx={{ flex: 1 }}
        >
          <Box
            sx={{
              height: "500px",
              p: 1,
              display: "flex",
              flexDirection: "column",
              cursor: "pointer",
              border: "1px solid",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
            onClick={() => handleWorkoutClick(workout, currentDate)}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {currentDate.format("MMM D")}
            </Typography>
            {workout && workout.id && (
              <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                <ReviewClientActivity
                  reviewingActivity={{ ...workout, user: { name: "Client" } }}
                  activityLoading={false}
                  hideReviewButton={true}
                />
              </Box>
            )}
            {selectedWorkout &&
              selectedWorkout.date?.isSame(currentDate, "day") && (
                <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                  {renderWorkoutContent(selectedWorkout)}
                </Box>
              )}
          </Box>
        </Grid2>
      );

      currentDay++;
    }

    // Add remaining empty cells for the last week
    while (currentWeek.length < 7) {
      currentWeek.push(
        <Grid2
          item
          xs={12 / 7}
          key={`empty-end-${currentWeek.length}`}
          sx={{ flex: 1 }}
        >
          <Box
            sx={{
              height: "500px",
              p: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          />
        </Grid2>
      );
    }
    weeks.push(
      <Grid2
        container
        spacing={0}
        justifyContent="space-between"
        alignItems="center"
        key={`week-${weeks.length}`}
      >
        {currentWeek}
      </Grid2>
    );

    return weeks;
  };

  return (
    <ThemeProvider theme={theme}>
      <>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton onClick={handlePrevMonth}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6" sx={{ mx: 2 }}>
              {currentDate.format("MMMM YYYY")}
            </Typography>
            <IconButton onClick={handleNextMonth}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
          <IconButton onClick={handleOpenMenu}>
            <MoreVertIcon />
          </IconButton>
        </Box>
        {menuItems}
        <Grid2
          container
          justifyContent="space-between"
          alignItems="center"
          spacing={0}
        >
          {[
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
          ].map((day) => (
            <Grid2 item key={day} xs sx={{ flex: 1 }}>
              <Box
                sx={{
                  p: 1,
                  textAlign: "center",
                  width: "100%",
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="subtitle2">{day}</Typography>
              </Box>
            </Grid2>
          ))}
        </Grid2>
        <Box
          sx={{
            height: "calc(100vh - 200px)",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#555",
            },
          }}
        >
          {renderCalendar()}
        </Box>
      </>
    </ThemeProvider>
  );
};

export default WorkoutCalendarView;
